import { AppMenuPoint, MenuPointAction } from '@agilox/common';
import { MenuPoint, NavColor, NavColors, NavTranslations } from '@agilox/ui-common';
import {
	Component,
	computed,
	ElementRef,
	HostListener,
	input,
	model,
	output,
	signal,
	viewChild,
} from '@angular/core';

@Component({
	selector: 'ui-nav',
	templateUrl: './nav.component.html',
	standalone: false,
})
export class NavComponent {
	logoPath = input<string | undefined>();
	items = input<MenuPoint[] | undefined>();
	navColor = input<NavColor>(NavColors.Dark);
	usePrimaryNavColor = input<boolean>(false);

	apps = input<AppMenuPoint[]>([]);

	activeApp = input<AppMenuPoint | undefined>();
	appNavVisible = input<boolean>(true);

	environment = input<string>('live');

	beta = input<boolean>(false);

	navTranslations = input<NavTranslations>();
	currentUrl = input<string>('');

	open = model<boolean>(false);

	callbackEvent = output<MenuPoint>();

	uiNav = viewChild<ElementRef>('uiNav');

	subMenuMarginTop = computed(() => {
		let marginTop = 0;
		if (this.appNavVisible() || this.logoPath()) {
			const children = this.uiNav()?.nativeElement.children;
			if (children.length > 0) {
				marginTop = children[0].clientHeight;
			}
		}
		return `-${marginTop}px`;
	});

	// Computed to get all menu items in order for keyboard navigation
	allMenuItems = computed(() => {
		const items = this.items() || [];
		const topItems = items.filter((item) => !item.position || item.position === 'top');
		const bottomItems = items.filter((item) => item.position === 'bottom');
		return [...topItems, ...bottomItems];
	});

	subNavOpen = computed(() => !!this.openedMenuPoint() && this.open());

	openedMenuPoint = signal<MenuPoint | undefined>(undefined);

	/** true if sub menu was opened by click
	 * used to decide if the submenu should be closed on mouseleave
	 * */
	subMenuOpenedByClick = signal(false);

	@HostListener('window:click', ['$event'])
	@HostListener('window:keydown.escape', ['$event'])
	clickOutside(event?: MouseEvent | KeyboardEvent) {
		const target = event?.target as HTMLElement;
		const hasUiNavClass = target?.closest('#ui-nav');
		if (!hasUiNavClass) {
			this.closeSubMenu();
		}
	}

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent) {
		// Check if app nav is open - if so, don't handle keyboard navigation
		const appNavOpen = document.querySelector('#app-nav')?.classList.contains('fixed');
		if (appNavOpen) {
			return;
		}

		// Check if focus is within submenu - if so, let submenu handle it
		const target = event.target as HTMLElement;
		if (target.closest('ui-sub-nav')) {
			return;
		}

		// Only handle keyboard navigation if focus is within the nav
		if (!this.uiNav()?.nativeElement.contains(event.target as Node)) {
			return;
		}

		const currentButton = target.closest('a, button') as HTMLElement | null;

		if (!currentButton) {
			return;
		}

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				this.navigateDown(currentButton);
				break;
			case 'ArrowUp':
				event.preventDefault();
				this.navigateUp(currentButton);
				break;
			case 'ArrowRight':
				event.preventDefault();
				this.navigateRight(currentButton);
				break;
			case 'ArrowLeft':
				event.preventDefault();
				this.navigateLeft();
				break;
			case 'Enter':
			case ' ':
				event.preventDefault();
				this.activateCurrentItem(currentButton);
				break;
			case 'Escape':
				event.preventDefault();
				this.handleEscape();
				break;
			case 'Home':
				event.preventDefault();
				this.navigateToFirst();
				break;
			case 'End':
				event.preventDefault();
				this.navigateToLast();
				break;
		}
	}

	private navigateDown(currentButton: HTMLElement) {
		const allButtons = this.getAllMainNavButtons();
		const currentIndex = allButtons.indexOf(currentButton);
		const nextIndex = Math.min(currentIndex + 1, allButtons.length - 1);
		const nextButton = allButtons[nextIndex];

		if (nextButton) {
			nextButton.focus();

			// Check if the new focused item has submenus, if not, close any open submenu
			const nextMenuItem = this.getMenuItemFromButton(nextButton);
			if (!nextMenuItem?.submenus && this.open()) {
				this.closeSubMenu();
			}
		}
	}

	private navigateUp(currentButton: HTMLElement) {
		const allButtons = this.getAllMainNavButtons();
		const currentIndex = allButtons.indexOf(currentButton);
		const prevIndex = Math.max(currentIndex - 1, 0);
		const prevButton = allButtons[prevIndex];

		if (prevButton) {
			prevButton.focus();

			// Check if the new focused item has submenus, if not, close any open submenu
			const prevMenuItem = this.getMenuItemFromButton(prevButton);
			if (!prevMenuItem?.submenus && this.open()) {
				this.closeSubMenu();
			}
		}
	}

	private navigateRight(currentButton: HTMLElement) {
		// Find which menu item this button corresponds to
		const menuItem = this.getMenuItemFromButton(currentButton);
		if (menuItem?.submenus && !this.open()) {
			this.openSubMenu(menuItem);
			// Focus first submenu item after DOM updates
			setTimeout(() => {
				const firstSubButton = this.uiNav()?.nativeElement.querySelector('[data-sub-nav-item="0"]');
				if (firstSubButton) {
					firstSubButton.focus();
				}
			}, 50);
		} else if (this.open()) {
			// Move focus to first submenu item
			setTimeout(() => {
				const firstSubButton = this.uiNav()?.nativeElement.querySelector('[data-sub-nav-item="0"]');
				if (firstSubButton) {
					firstSubButton.focus();
				}
			}, 50);
		}
	}

	private navigateLeft() {
		if (this.open()) {
			this.closeSubMenu();
		}
	}

	private navigateToFirst() {
		const allButtons = this.getAllMainNavButtons();
		const firstButton = allButtons[0];

		if (firstButton) {
			firstButton.focus();

			// Check if the first item has submenus, if not, close any open submenu
			const firstMenuItem = this.getMenuItemFromButton(firstButton);
			if (!firstMenuItem?.submenus && this.open()) {
				this.closeSubMenu();
			}
		}
	}

	private navigateToLast() {
		const allButtons = this.getAllMainNavButtons();
		const lastButton = allButtons[allButtons.length - 1];

		if (lastButton) {
			lastButton.focus();

			// Check if the last item has submenus, if not, close any open submenu
			const lastMenuItem = this.getMenuItemFromButton(lastButton);
			if (!lastMenuItem?.submenus && this.open()) {
				this.closeSubMenu();
			}
		}
	}

	private activateCurrentItem(currentButton: HTMLElement) {
		// Simulate click on the button
		currentButton.click();
	}

	private handleEscape() {
		if (this.open()) {
			this.closeSubMenu();
		}
	}

	private getAllMainNavButtons(): HTMLElement[] {
		return Array.from(
			this.uiNav()?.nativeElement.querySelectorAll(
				'ui-nav-list-entry a, ui-nav-list-entry button'
			) || []
		);
	}

	private getMenuItemFromButton(button: HTMLElement): MenuPoint | undefined {
		const navEntry = button.closest('ui-nav-list-entry');
		const dataId = navEntry?.getAttribute('data-id');
		if (!dataId) return undefined;

		return this.allMenuItems().find((item) => item.url === dataId);
	}

	/**
	 * handles menu point action
	 * @param event
	 * If the menu point has submenus, the submenu is opened
	 * If the menu point has no submenus, the url is navigated to
	 * If the menu point has callbackEvent = true, the callbackEvent event is emitted
	 */
	onMenuPointAction(event: MenuPointAction) {
		if (event.menuPoint.callback) {
			this.callbackEvent.emit(event.menuPoint);
			return;
		}
		this.subMenuOpenedByClick.set(event.click);
		if (event.menuPoint.submenus) {
			this.openSubMenu(event.menuPoint);
		} else {
			this.closeSubMenu();
		}
	}

	/**
	 * handles mouse enter
	 * If the submenu was opened by clicking, the submenu is not closed on mouse leave
	 * else the submenu is closed on mouse leave
	 */
	onMouseLeave() {
		if (!this.subMenuOpenedByClick()) {
			this.closeSubMenu();
		}
	}

	openSubMenu(menuPoint: MenuPoint) {
		this.openedMenuPoint.set(menuPoint);
		this.open.set(true);
	}

	closeSubMenu() {
		this.openedMenuPoint.set(undefined);
		this.open.set(false);
	}
}
